import "bootstrap";

import * as Sentry from "@sentry/browser";
import { enableMapSet } from "immer";
import React from "react";
import { createRoot } from "react-dom/client";
import submitButton from "utils/submitButton";
import initAdmin from "widgets/AdminViewEnhancers";
import initCategoryEditor from "widgets/CategoryEditor";

import { App } from "~/App";

enableMapSet();

// Browser error reporting (replaces the raven-js setup removed with the
// backend raven→sentry-sdk migration). Default integrations already capture
// onerror and unhandled promise rejections. Tracing deliberately off.
if (process.env.NODE_ENV === "production" && DJ_CONST.SENTRY_PUBLIC_DSN) {
  Sentry.init({
    dsn: DJ_CONST.SENTRY_PUBLIC_DSN,
    environment: DJ_CONST.ENVIRONMENT_TAG || "unset",
  });
  if (DJ_CONST.user) {
    // A stable id is always attached; email/name follow the same per-env
    // opt-in as the backend's send_default_pii (DJANGO_SENTRY_SEND_PII).
    const sentryUser = { id: String(DJ_CONST.user.id) };
    if (DJ_CONST.SENTRY_SEND_PII) {
      sentryUser.email = DJ_CONST.user.email;
      sentryUser.username = DJ_CONST.user.name;
    }
    Sentry.setUser(sentryUser);
    // Errors from impersonated sessions are the admin's, not the user's —
    // keep reporting them (unlike Mixpanel, which disables tracking), but
    // mark them so they aren't misattributed during triage.
    if (DJ_CONST.user.is_hijacked_user) {
      Sentry.setTag("hijacked", "true");
    }
  }
}

function init() {
  // IE fix
  HTMLCollection.prototype.forEach = Array.prototype.forEach;
  NodeList.prototype.forEach = Array.prototype.forEach;

  submitButton();

  // Initialize Mixpanel
  if (DJ_CONST.user) {
    if (DJ_CONST.user.is_hijacked_user) {
      mixpanel.disable();
    }

    mixpanel.identify(DJ_CONST.user.id);
    mixpanel.people.set({
      $email: DJ_CONST.user.email,
      $phone: DJ_CONST.user.phone_number,
      $first_name: DJ_CONST.user.first_name,
      $last_name: DJ_CONST.user.last_name,
      $created: DJ_CONST.user.date_joined,
      "Is Staff": DJ_CONST.user.is_staff,
      Company: DJ_CONST.user.company,
    });
  }
}

function initApp() {
  createRoot(document.getElementById("app")).render(<App />);
}

export { init, initApp, initAdmin, initCategoryEditor };
